import React, { useState, useEffect, useCallback } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import {
    Plus,
    Edit,
    Trash2,
    Eye,
    Heart,
    Save,
    X,
    Image,
    Tag
} from 'lucide-react';

const BlogManagement = () => {
    const { canManageBlog } = usePermissions();
    const [blogs, setBlogs] = useState([]);
    const [editingBlog, setEditingBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filter, setFilter] = useState({ status: '', category: '' });

    const categories = [
        { value: 'teknoloji', label: 'Teknoloji' },
        { value: 'mobil-uygulama', label: 'Mobil Uygulama' },
        { value: 'web-gelistirme', label: 'Web Geliştirme' },
        { value: 'tubitak', label: 'TÜBİTAK' },
        { value: 'girisimcilik', label: 'Girişimcilik' },
        { value: 'tasarim', label: 'Tasarım' },
        { value: 'pazarlama', label: 'Pazarlama' },
        { value: 'genel', label: 'Genel' }
    ];

    const statusOptions = [
        { value: 'draft', label: 'Taslak', color: 'gray' },
        { value: 'published', label: 'Yayınlandı', color: 'green' },
        { value: 'archived', label: 'Arşivlendi', color: 'red' }
    ];

    const loadBlogs = useCallback(async () => {
        try {
            setLoading(true);
            const baseUrl = window.location.hostname === 'localhost'
                ? 'http://localhost:3001'
                : 'https://cezecdev.netlify.app/';

            let url = `${baseUrl}/api/blog/admin?page=${currentPage}&limit=10`;
            if (filter.status) url += `&status=${filter.status}`;
            if (filter.category) url += `&category=${filter.category}`;

            const response = await fetch(url);
            if (response.ok) {
                const data = await response.json();
                setBlogs(data.blogs);
                setTotalPages(data.totalPages);
            } else {
                console.error('Blog yükleme hatası:', response.status, response.statusText);
                alert(`Blog yükleme hatası: ${response.status} ${response.statusText}`);
            }
        } catch (error) {
            console.error('Blog yükleme hatası:', error);
            alert('Blog yükleme hatası: ' + error.message);
        } finally {
            setLoading(false);
        }
    }, [currentPage, filter]);

    useEffect(() => {
        loadBlogs();
    }, [loadBlogs]);

    const saveBlog = async (blogData) => {
        try {
            const baseUrl = window.location.hostname === 'localhost'
                ? 'http://localhost:3001'
                : 'https://cezecdev.netlify.app/';

            const url = blogData._id ? `${baseUrl}/api/blog/${blogData._id}` : `${baseUrl}/api/blog`;
            const method = blogData._id ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(blogData)
            });

            if (response.ok) {
                loadBlogs();
                setEditingBlog(null);
                alert(blogData._id ? 'Blog güncellendi!' : 'Blog oluşturuldu!');
            } else {
                console.error('Blog kaydetme hatası:', response.status, response.statusText);
                try {
                    const errorData = await response.json();
                    alert(`Hata: ${errorData.message}`);
                } catch {
                    alert(`Hata: ${response.status} ${response.statusText}`);
                }
            }
        } catch (error) {
            console.error('Blog kaydetme hatası:', error);
            alert('Blog kaydedilirken hata oluştu: ' + error.message);
        }
    };

    const deleteBlog = async (id) => {
        if (window.confirm('Bu blog yazısını kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!')) {
            try {
                const baseUrl = window.location.hostname === 'localhost'
                    ? 'http://localhost:3001'
                    : 'https://cezecdev.netlify.app/';

                const response = await fetch(`${baseUrl}/api/blog/${id}`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                const data = await response.json();

                if (response.ok && data.success) {
                    // Silinen blog'u listeden hemen kaldır
                    setBlogs(prevBlogs => {
                        const newBlogs = prevBlogs.filter(blog => blog._id !== id);

                        // Eğer mevcut sayfada blog kalmadıysa ve ilk sayfa değilse önceki sayfaya git
                        if (newBlogs.length === 0 && currentPage > 1) {
                            setCurrentPage(prev => prev - 1);
                        }

                        return newBlogs;
                    });

                    alert('Blog yazısı başarıyla silindi!');

                    // Liste yenilenmesi için kısa bir gecikme sonra loadBlogs çağır
                    setTimeout(() => {
                        loadBlogs();
                    }, 500);
                } else {
                    throw new Error(data.message || 'Blog silinirken hata oluştu');
                }
            } catch (error) {
                console.error('Blog silme hatası:', error);
                alert('Blog silinirken hata oluştu: ' + error.message);
            }
        }
    };

    const getStatusColor = (status) => {
        const statusObj = statusOptions.find(s => s.value === status);
        return statusObj ? statusObj.color : 'gray';
    };

    const getStatusLabel = (status) => {
        const statusObj = statusOptions.find(s => s.value === status);
        return statusObj ? statusObj.label : status;
    };

    const getCategoryLabel = (category) => {
        const categoryObj = categories.find(c => c.value === category);
        return categoryObj ? categoryObj.label : category;
    };

    // Yetki kontrolü
    if (!canManageBlog()) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Yetkisiz Erişim</h2>
                    <p className="text-gray-600">Blog yönetimi için yetkiniz bulunmamaktadır.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold text-gray-800">Blog Yönetimi</h1>
                <button
                    onClick={() => setEditingBlog({
                        title: '',
                        excerpt: '',
                        content: '',
                        category: 'teknoloji',
                        status: 'draft',
                        author: { name: 'Admin', avatar: '', bio: '' },
                        tags: [],
                        featuredImage: '',
                        isFeatured: false,
                        seo: { metaTitle: '', metaDescription: '', keywords: [] }
                    })}
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                >
                    <Plus size={16} /> Yeni Blog Yazısı
                </button>
            </div>

            {/* Filtreler */}
            <div className="bg-white rounded-lg shadow-md p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <select
                        value={filter.status}
                        onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                        className="px-3 py-2 border rounded-lg"
                    >
                        <option value="">Tüm Durumlar</option>
                        {statusOptions.map(status => (
                            <option key={status.value} value={status.value}>{status.label}</option>
                        ))}
                    </select>

                    <select
                        value={filter.category}
                        onChange={(e) => setFilter({ ...filter, category: e.target.value })}
                        className="px-3 py-2 border rounded-lg"
                    >
                        <option value="">Tüm Kategoriler</option>
                        {categories.map(category => (
                            <option key={category.value} value={category.value}>{category.label}</option>
                        ))}
                    </select>

                    <button
                        onClick={() => setFilter({ status: '', category: '' })}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
                    >
                        Filtreleri Temizle
                    </button>
                </div>
            </div>

            {/* Blog Listesi */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Başlık
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Kategori
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Durum
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    İstatistikler
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Tarih
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    İşlemler
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {blogs.map(blog => (
                                <tr key={blog._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10">
                                                {blog.featuredImage ? (
                                                    <img className="h-10 w-10 rounded-lg object-cover" src={blog.featuredImage} alt="" />
                                                ) : (
                                                    <div className="h-10 w-10 rounded-lg bg-gray-300 flex items-center justify-center">
                                                        <Image className="text-gray-500" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">{blog.title}</div>
                                                <div className="text-sm text-gray-500">{blog.excerpt.substring(0, 50)}...</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {getCategoryLabel(blog.category)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${getStatusColor(blog.status)}-100 text-${getStatusColor(blog.status)}-800`}>
                                            {getStatusLabel(blog.status)}
                                        </span>
                                        {blog.isFeatured && (
                                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                Öne Çıkan
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div className="flex items-center gap-4">
                                            <span><Eye className="inline mr-1" size={14} /> {blog.views}</span>
                                            <span><Heart className="inline mr-1" size={14} /> {blog.likes}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(blog.createdAt).toLocaleDateString('tr-TR')}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setEditingBlog(blog)}
                                                className="text-blue-600 hover:text-blue-900"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                onClick={() => deleteBlog(blog._id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                            {blog.status === 'published' && (
                                                <a
                                                    href={`../../blog-site/post.html?slug=${blog.slug}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-green-600 hover:text-green-900"
                                                >
                                                    <Eye size={16} />
                                                </a>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-center">
                    <div className="flex gap-2">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`px-3 py-2 rounded ${page === currentPage
                                    ? 'bg-green-500 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                {page}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Blog Düzenleme Modal */}
            {editingBlog && (
                <BlogEditModal
                    blog={editingBlog}
                    categories={categories}
                    statusOptions={statusOptions}
                    onSave={saveBlog}
                    onClose={() => setEditingBlog(null)}
                />
            )}
        </div>
    );
};

// Blog Düzenleme Modal Komponenti
const BlogEditModal = ({ blog, categories, statusOptions, onSave, onClose }) => {
    const [formData, setFormData] = useState(blog);
    const [tagInput, setTagInput] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const addTag = () => {
        if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
            setFormData({
                ...formData,
                tags: [...formData.tags, tagInput.trim()]
            });
            setTagInput('');
        }
    };

    const removeTag = (tagToRemove) => {
        setFormData({
            ...formData,
            tags: formData.tags.filter(tag => tag !== tagToRemove)
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">
                            {blog._id ? 'Blog Düzenle' : 'Yeni Blog Yazısı'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Başlık */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Başlık *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                        </div>

                        {/* Özet */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Özet *
                            </label>
                            <textarea
                                required
                                rows={3}
                                value={formData.excerpt}
                                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                maxLength={300}
                            />
                            <div className="text-sm text-gray-500 mt-1">
                                {formData.excerpt.length}/300 karakter
                            </div>
                        </div>

                        {/* İçerik */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                İçerik *
                            </label>
                            <textarea
                                required
                                rows={15}
                                value={formData.content}
                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-sm"
                                placeholder="Markdown formatında yazabilirsiniz..."
                            />
                        </div>

                        {/* Kategori ve Durum */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Kategori *
                                </label>
                                <select
                                    required
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                >
                                    {categories.map(category => (
                                        <option key={category.value} value={category.value}>
                                            {category.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Durum *
                                </label>
                                <select
                                    required
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                >
                                    {statusOptions.map(status => (
                                        <option key={status.value} value={status.value}>
                                            {status.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Öne Çıkan Görsel */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Öne Çıkan Görsel URL
                            </label>
                            <input
                                type="url"
                                value={formData.featuredImage}
                                onChange={(e) => setFormData({ ...formData, featuredImage: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                        </div>

                        {/* Etiketler */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Etiketler
                            </label>
                            <div className="flex gap-2 mb-2">
                                <input
                                    type="text"
                                    value={tagInput}
                                    onChange={(e) => setTagInput(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    placeholder="Etiket ekle..."
                                />
                                <button
                                    type="button"
                                    onClick={addTag}
                                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg"
                                >
                                    <Tag size={16} />
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {formData.tags.map(tag => (
                                    <span
                                        key={tag}
                                        className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                                    >
                                        #{tag}
                                        <button
                                            type="button"
                                            onClick={() => removeTag(tag)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <X size={12} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Yazar Bilgileri */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Yazar Adı *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.author.name}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    author: { ...formData.author, name: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                        </div>

                        {/* Öne Çıkan */}
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="isFeatured"
                                checked={formData.isFeatured}
                                onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <label htmlFor="isFeatured" className="ml-2 block text-sm text-gray-900">
                                Öne çıkan yazı olarak işaretle
                            </label>
                        </div>

                        {/* Butonlar */}
                        <div className="flex justify-end gap-4 pt-6 border-t">
                            <button
                                type="button"
                                onClick={onClose}
                                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg"
                            >
                                İptal
                            </button>
                            <button
                                type="submit"
                                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg flex items-center gap-2"
                            >
                                <Save size={16} /> Kaydet
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default BlogManagement;